// Physically slices a baked arrangement render (one continuous WAV/AIFF per
// track, rendered from beat 0 at the seed tempo) into one real audio file per
// arrangement clip — and cuts the part of a sample a pulled clip plays.
//
// Why physical slicing rather than clip start/end markers: a marker-based slice
// leans on Live's beat<->sample mapping of the imported render, which auto-warps
// and drifts. Cutting the actual PCM means each clip is a self-contained file
// whose audio starts at sample 0 — Import can drop it unwarped at its beat with
// no marker offset, so it always lines up.
//
// The cut is a verbatim byte copy on frame boundaries: no sample decoding, no
// endianness or bit-depth handling, and the source's own format chunk (WAV
// `fmt `, AIFF `COMM`/`FVER`) is copied into each slice so the container stays
// valid for any PCM the renderer emits (observed: 24-bit/48k AIFF, and WAV).
// That verbatim copy is the point: re-encoding here would quietly drop a
// 24-bit render to 16.
//
// Uint8Array and DataView throughout, never Buffer — the extension slices
// renders inside Live and the viz app slices samples downloaded from Audiotool
// in a browser tab, and both run exactly this.

export interface ClipRange {
  startTime: number; // arrangement beat the clip starts on
  endTime: number; // arrangement beat the clip ends on
}

export interface AudioSlice {
  /** Index into the input `clips` array, so callers can map back to name/colour. */
  sourceIndex: number;
  /** Arrangement beat to place the slice at (unchanged from the source clip). */
  startTime: number;
  /** A complete, self-contained WAV/AIFF file for just this clip. */
  bytes: Uint8Array;
}

/**
 * Cut `src` into one file per clip. Clips beginning past the render are dropped;
 * clips running past its end are clamped to it. Returns [] if the audio can't be
 * parsed or the tempo is invalid.
 */
export function sliceArrangementAudio(src: Uint8Array, clips: ClipRange[], tempo: number): AudioSlice[] {
  const info = parse(src);
  if (!info || !(tempo > 0)) return [];

  const framesPerBeat = (info.sampleRate * 60) / tempo;
  const totalFrames = Math.floor(info.sound.length / info.bytesPerFrame);
  const out: AudioSlice[] = [];

  for (let i = 0; i < clips.length; i++) {
    const c = clips[i]!;
    const startFrame = Math.max(0, Math.round(c.startTime * framesPerBeat));
    let endFrame = Math.round(c.endTime * framesPerBeat);
    if (startFrame >= totalFrames) continue; // begins past the render — no audio
    if (endFrame > totalFrames) endFrame = totalFrames; // clamp to render end
    if (endFrame <= startFrame) continue;

    const slice = info.sound.subarray(startFrame * info.bytesPerFrame, endFrame * info.bytesPerFrame);
    out.push({ sourceIndex: i, startTime: c.startTime, bytes: encode(info, slice, endFrame - startFrame) });
  }
  return out;
}

/**
 * Cut the frames between two FRACTIONS of `src` into a self-contained file —
 * the part of an Audiotool sample a pulled clip plays (shared/audiotool/
 * pull-plan.ts works in sample fractions so no tempo enters the cut). Same
 * verbatim frame-boundary copy as sliceArrangementAudio. Fractions are clamped
 * to [0, 1]; null for an empty window or bytes that aren't WAV/AIFF.
 */
export function sliceFraction(src: Uint8Array, from: number, to: number): Uint8Array | null {
  const info = parse(src);
  if (!info) return null;
  const total = Math.floor(info.sound.length / info.bytesPerFrame);
  const clamp = (x: number): number => Math.min(1, Math.max(0, x));
  const startFrame = Math.round(clamp(from) * total);
  const endFrame = Math.round(clamp(to) * total);
  if (endFrame <= startFrame) return null;
  const slice = info.sound.subarray(startFrame * info.bytesPerFrame, endFrame * info.bytesPerFrame);
  return encode(info, slice, endFrame - startFrame);
}

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

function viewOf(b: Uint8Array): DataView {
  return new DataView(b.buffer, b.byteOffset, b.byteLength);
}

function ascii(b: Uint8Array, from: number, to: number): string {
  let s = "";
  for (let i = from; i < to && i < b.length; i++) s += String.fromCharCode(b[i]!);
  return s;
}

function writeAscii(b: Uint8Array, offset: number, tag: string): void {
  for (let i = 0; i < tag.length; i++) b[offset + i] = tag.charCodeAt(i);
}

function concat(parts: ReadonlyArray<Uint8Array>): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------

interface ParsedAudio {
  kind: "wav" | "aiff";
  sampleRate: number;
  bytesPerFrame: number;
  /** Raw interleaved sample bytes (the WAV `data` / AIFF `SSND` payload). */
  sound: Uint8Array;
  /** WAV: the verbatim `fmt ` chunk (header + body). */
  fmtChunk?: Uint8Array;
  /** AIFF: "AIFF" or "AIFC". */
  formType?: string;
  /** AIFF: the verbatim `FVER` chunk if present (required for AIFC). */
  fverChunk?: Uint8Array | null;
  /** AIFF: the verbatim `COMM` chunk; numSampleFrames is patched per slice. */
  commChunk?: Uint8Array;
}

function parse(buf: Uint8Array): ParsedAudio | null {
  if (buf.length < 12) return null;
  const head = ascii(buf, 0, 4);
  const form = ascii(buf, 8, 12);
  if (head === "RIFF" && form === "WAVE") return parseWav(buf);
  if (head === "FORM" && (form === "AIFF" || form === "AIFC")) return parseAiff(buf);
  return null;
}

function parseWav(buf: Uint8Array): ParsedAudio | null {
  const v = viewOf(buf);
  let fmtChunk: Uint8Array | null = null;
  let sound: Uint8Array | null = null;
  let channels = 0;
  let sampleRate = 0;
  let bits = 0;

  let off = 12;
  while (off + 8 <= buf.length) {
    const id = ascii(buf, off, off + 4);
    const size = v.getUint32(off + 4, true);
    const body = off + 8;
    const end = Math.min(body + size, buf.length);
    if (id === "fmt " && body + 16 <= buf.length) {
      fmtChunk = buf.subarray(off, body + size);
      channels = v.getUint16(body + 2, true);
      sampleRate = v.getUint32(body + 4, true);
      bits = v.getUint16(body + 14, true);
    } else if (id === "data") {
      sound = buf.subarray(body, end);
    }
    off = body + size + (size & 1); // chunks are word-aligned
  }

  const bytesPerFrame = channels * (bits / 8);
  if (!fmtChunk || !sound || !sampleRate || !bytesPerFrame) return null;
  return { kind: "wav", sampleRate, bytesPerFrame, sound, fmtChunk };
}

function parseAiff(buf: Uint8Array): ParsedAudio | null {
  const v = viewOf(buf);
  const formType = ascii(buf, 8, 12);
  let fverChunk: Uint8Array | null = null;
  let commChunk: Uint8Array | null = null;
  let sound: Uint8Array | null = null;
  let channels = 0;
  let sampleRate = 0;
  let bits = 0;

  let off = 12;
  while (off + 8 <= buf.length) {
    const id = ascii(buf, off, off + 4);
    const size = v.getUint32(off + 4, false);
    const body = off + 8;
    const end = Math.min(body + size, buf.length);
    if (id === "FVER") {
      fverChunk = buf.subarray(off, body + size);
    } else if (id === "COMM" && body + 18 <= buf.length) {
      commChunk = buf.subarray(off, body + size);
      channels = v.getUint16(body, false);
      bits = v.getUint16(body + 6, false);
      sampleRate = readExtendedFloat80(v, body + 8);
    } else if (id === "SSND") {
      // SSND body = offset(4) + blockSize(4) + sound data.
      sound = buf.subarray(body + 8, end);
    }
    off = body + size + (size & 1);
  }

  const bytesPerFrame = channels * (bits / 8);
  if (!commChunk || !sound || !sampleRate || !bytesPerFrame) return null;
  return { kind: "aiff", sampleRate, bytesPerFrame, sound, formType, fverChunk, commChunk };
}

/** Decode an 80-bit IEEE-754 extended float (AIFF sample rate), big-endian. */
function readExtendedFloat80(v: DataView, off: number): number {
  const exponent = ((v.getUint8(off) & 0x7f) << 8) | v.getUint8(off + 1);
  const mantissa = v.getUint32(off + 2, false) * 2 ** 32 + v.getUint32(off + 6, false);
  if (exponent === 0 && mantissa === 0) return 0;
  const sign = v.getUint8(off) & 0x80 ? -1 : 1;
  return sign * mantissa * 2 ** (exponent - 16383 - 63);
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

function encode(info: ParsedAudio, slice: Uint8Array, frames: number): Uint8Array {
  return info.kind === "wav" ? encodeWav(info, slice) : encodeAiff(info, slice, frames);
}

function encodeWav(info: ParsedAudio, slice: Uint8Array): Uint8Array {
  const fmt = padChunk(info.fmtChunk!);
  const dataHeader = new Uint8Array(8);
  writeAscii(dataHeader, 0, "data");
  viewOf(dataHeader).setUint32(4, slice.length, true);
  const dataPad = slice.length & 1 ? new Uint8Array(1) : EMPTY;

  const wave = new Uint8Array(4);
  writeAscii(wave, 0, "WAVE");
  const body = concat([wave, fmt, dataHeader, slice, dataPad]);
  const riff = new Uint8Array(8);
  writeAscii(riff, 0, "RIFF");
  viewOf(riff).setUint32(4, body.length, true);
  return concat([riff, body]);
}

function encodeAiff(info: ParsedAudio, slice: Uint8Array, frames: number): Uint8Array {
  // Copy COMM verbatim and patch numSampleFrames (chunk offset 8 + body offset 2).
  const comm = Uint8Array.from(info.commChunk!);
  viewOf(comm).setUint32(10, frames, false);
  const commPadded = padChunk(comm);
  const fverPadded = info.fverChunk ? padChunk(info.fverChunk) : EMPTY;

  const ssndHeader = new Uint8Array(16);
  writeAscii(ssndHeader, 0, "SSND");
  const sv = viewOf(ssndHeader);
  sv.setUint32(4, 8 + slice.length, false); // size = offset(4)+blockSize(4)+data
  sv.setUint32(8, 0, false); // offset
  sv.setUint32(12, 0, false); // blockSize
  const ssndPad = slice.length & 1 ? new Uint8Array(1) : EMPTY;

  const form = new Uint8Array(4);
  writeAscii(form, 0, info.formType ?? "AIFF");
  const body = concat([form, fverPadded, commPadded, ssndHeader, slice, ssndPad]);
  const formHeader = new Uint8Array(8);
  writeAscii(formHeader, 0, "FORM");
  viewOf(formHeader).setUint32(4, body.length, false);
  return concat([formHeader, body]);
}

const EMPTY = new Uint8Array(0);

/** Word-align a chunk by appending a pad byte when its data length is odd. */
function padChunk(chunk: Uint8Array): Uint8Array {
  const dataSize = chunk.length - 8;
  return dataSize & 1 ? concat([chunk, new Uint8Array(1)]) : chunk;
}
